/**
 * A confirmation before a consequential edit (decisions-log V125): editing a month whose invoice is
 * issued or whose payout is approved or paid asks first. `useConfirm` keeps the open request and
 * renders the dialog; the screen places `dialog` once.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { Dialog } from './Dialog';

export interface ConfirmRequest {
  readonly title: string;
  readonly text: string;
  /** The confirm button, e.g. «Изменить». */
  readonly okLabel: string;
  readonly onConfirm: () => void;
  /** A second way out besides confirming, e.g. «На август» (V284). */
  readonly alt?: { readonly label: string; readonly onClick: () => void };
}

interface ConfirmDialogProps {
  readonly request: ConfirmRequest;
  readonly onClose: () => void;
}

export function ConfirmDialog({ request, onClose }: ConfirmDialogProps) {
  const confirm = () => {
    onClose();
    request.onConfirm();
  };
  const alt = request.alt;
  const chooseAlt = () => {
    onClose();
    alt?.onClick();
  };
  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={request.title}
      description={request.text}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Отмена
          </Button>
          {alt && (
            <Button variant="secondary" size="sm" onClick={chooseAlt}>
              {alt.label}
            </Button>
          )}
          <Button variant="ink" size="sm" onClick={confirm}>
            {request.okLabel}
          </Button>
        </>
      }
    />
  );
}

export interface Confirm {
  readonly ask: (request: ConfirmRequest) => void;
  readonly dialog: ReactNode;
}

export function useConfirm(): Confirm {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const dialog = request ? <ConfirmDialog request={request} onClose={() => setRequest(null)} /> : null;
  return { ask: setRequest, dialog };
}
